import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { PageHeader, Card } from "@/components/admin/ui";
import { TrendingUp, MessageSquare, Calendar } from "lucide-react";

export default function AnalyticsPage() {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiClient.get("/analytics/overview").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="text-stone-muted">Loading…</div>;

    const maxDaily = Math.max(1, ...data.inquiries_per_day.map((d) => d.count));
    const maxService = Math.max(1, ...data.inquiries_by_service.map((d) => d.count));

    return (
        <>
            <PageHeader title="Analytics" subtitle="Inquiry trends and content breakdown." />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <Card className="p-5 md:p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-stone-muted">Inquiries · last 7 days</div><div className="font-serif text-5xl mt-2 inline-flex items-center gap-3">{data.inquiries_7d}<TrendingUp size={20} className="text-walnut" /></div></Card>
                <Card className="p-5 md:p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-stone-muted">Inquiries · last 30 days</div><div className="font-serif text-5xl mt-2 inline-flex items-center gap-3">{data.inquiries_30d}<Calendar size={20} className="text-walnut" /></div></Card>
                <Card className="p-5 md:p-6"><div className="text-[10px] uppercase tracking-[0.2em] text-stone-muted">Inquiries total tracked</div><div className="font-serif text-5xl mt-2 inline-flex items-center gap-3">{data.inquiries_per_day.reduce((s, d) => s + d.count, 0)}<MessageSquare size={20} className="text-walnut" /></div></Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-5 md:p-6">
                    <h3 className="font-serif text-xl mb-4">Inquiries · last 14 days</h3>
                    {data.inquiries_per_day.length === 0 ? <div className="text-stone-muted text-sm">No data yet.</div> : (
                        <div className="space-y-2">
                            {data.inquiries_per_day.map((d) => (
                                <div key={d.day} className="flex items-center gap-3 text-xs">
                                    <div className="w-20 text-stone-muted">{d.day}</div>
                                    <div className="flex-1 h-6 bg-sand dark:bg-stone-800 relative">
                                        <div className="h-full bg-walnut" style={{ width: `${(d.count / maxDaily) * 100}%` }} />
                                    </div>
                                    <div className="w-6 text-right text-stone-muted">{d.count}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="p-5 md:p-6">
                    <h3 className="font-serif text-xl mb-4">Popular services (by inquiry)</h3>
                    {data.inquiries_by_service.length === 0 ? <div className="text-stone-muted text-sm">No data yet.</div> : (
                        <div className="space-y-2">
                            {data.inquiries_by_service.map((d) => (
                                <div key={d.service} className="flex items-center gap-3 text-xs">
                                    <div className="w-40 truncate text-stone-muted">{d.service}</div>
                                    <div className="flex-1 h-6 bg-sand dark:bg-stone-800 relative">
                                        <div className="h-full bg-walnut-light" style={{ width: `${(d.count / maxService) * 100}%` }} />
                                    </div>
                                    <div className="w-6 text-right text-stone-muted">{d.count}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="p-5 md:p-6 md:col-span-2">
                    <h3 className="font-serif text-xl mb-4">Gallery distribution by category</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {data.gallery_by_category.map((d) => (
                            <div key={d.category} className="bg-sand dark:bg-stone-800 p-4">
                                <div className="text-[10px] uppercase tracking-widest text-walnut-light">{d.category}</div>
                                <div className="font-serif text-3xl mt-1">{d.count}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </>
    );
}
